import { useMemo } from 'react';

import {
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  FAIconName,
  FireflyButton,
  ToastMessage,
} from '@equip.health/ui';

import { useEditBasicInfoForm } from '~/lib/hooks/useEditBasicInfoForm';
import useUnsavedChangesGuard from '~/lib/hooks/useUnsavedChangesGuard';

import PatientProfileAddress from './PatientProfileAddress';
import ProfileDemographics from './ProfileDemographics';
import ProfilePersonalInfo from './ProfilePersonalInfo';
import EditEmailWarningModal from '../contact/EditEmailWarningModal';
import { isValidZipCodeFormat } from '~/lib/util/patientContact.util';
import SurveyDataWarning from '~/components/common/SurveyDataWarning';

interface EditBasicInfoFormProps {
  appendSurveyResult: any;
  patientId: string;
  basicInfo: Patient;
  onDiscard: () => void;
  onUpdate: () => void;
}

const EditBasicInfoForm = ({
  appendSurveyResult,
  basicInfo,
  onDiscard,
  onUpdate,
  patientId,
}: EditBasicInfoFormProps) => {
  const TOP_LEVEL_ID = 'patient-profile-edit-form';

  const {
    formData,
    updateField,
    updatePersonalInfo,
    handleSaveForm,
    updateSection,
  } = useEditBasicInfoForm(basicInfo, patientId, onUpdate);

  const { UnsavedChangesModal, guardedDismiss } = useUnsavedChangesGuard({
    isDirty: formData.hasUnsavedChanges,
    onDiscard,
  });

  const formattedAppendSurveyResult = useMemo(() => {
    const result = { ...appendSurveyResult };
    delete result.country;
    delete result.county;

    return result;
  }, [appendSurveyResult]);

  const isZipValid = () => {
    const shippingAddressZipCode = formData?.shippingAddress?.zipCode;
    const mainAddressZipCode = formData?.mainAddress?.zipCode;

    const isShippingAddressZipCodeValid = isValidZipCodeFormat(
      shippingAddressZipCode,
    );
    const isMainAddressZipCodeValid = isValidZipCodeFormat(mainAddressZipCode);

    return isShippingAddressZipCodeValid && isMainAddressZipCodeValid;
  };

  const shouldDisableSave =
    Object.entries(formData?.personalInfo?.invalidFields).some(
      ([key, value]) =>
        value === true &&
        formData?.personalInfo[key as keyof ProfilePersonalInfoData],
    ) || !isZipValid();

  const isSaveDisabled =
    shouldDisableSave ||
    (!formData.hasUnsavedChanges &&
      Object.keys(formattedAppendSurveyResult).length === 0);

  return (
    <>
      {Object.keys(formattedAppendSurveyResult).length > 0 &&
        !formData.hasUnsavedChanges && <SurveyDataWarning isEditMode />}
      <EditEmailWarningModal
        id="patient-basic-info"
        isOpen={formData.emailChangeWarningModalOpen}
        onClose={() => updateField('emailChangeWarningModalOpen', false)}
        onConfirm={handleSaveForm}
      />
      <ChakraSimpleGrid
        columns={3}
        margin="18px 0px 24px 0px"
        padding="0px 8px"
        spacing="36px"
      >
        {UnsavedChangesModal}
        <ChakraGridItem colSpan={1}>
          <ProfilePersonalInfo
            appendSurveyResult={appendSurveyResult}
            basicInfoEmail={basicInfo.email}
            hasEquipApp={basicInfo.hasAuthId}
            personalInfo={formData.personalInfo}
            phonesData={formData.phonesField}
            updatePersonalInfo={updatePersonalInfo}
            updateSection={updateSection}
          />
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <PatientProfileAddress
            appendSurveyResult={appendSurveyResult}
            mainAddress={formData.mainAddress}
            shippingAddress={formData.shippingAddress}
            updateField={updateField}
            updateSection={updateSection}
          />
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <ProfileDemographics
            appendSurveyResult={appendSurveyResult}
            data={formData.demographics}
            updateSection={updateSection}
          />
        </ChakraGridItem>
      </ChakraSimpleGrid>
      {formData.isError && (
        <ToastMessage
          icon={FAIconName.exclamationTriangleRegular}
          id={`${TOP_LEVEL_ID}__warning`}
          isFullWidth
          text={
            "We're sorry, we were unable to save your changes. Please try again."
          }
          variant="critical"
        />
      )}
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="24px"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}__cancel-button`}
          leftIcon={FAIconName.XmarkRegular}
          onClick={guardedDismiss}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__save-button`}
          isDisabled={isSaveDisabled}
          isLoading={formData.isLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={handleSaveForm}
        >
          Save changes
        </FireflyButton>
      </ChakraFlex>
    </>
  );
};

export default EditBasicInfoForm;
